import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ErrorState } from "@/components/shared/ErrorState";
import { PhysicalClientAddressList } from "@/components/shared/address/PhysicalClientAddressList";
import { PhysicalClientStatus, physicalClientsApi, UpdatePhysicalClientDto } from "@/lib/api";
import { FieldError, getCustomerRegistrationErrorMessage, getFieldErrors } from "@/lib/customerRegistrationError";
import { formatDateTime } from "@/lib/formatters";
import { DocumentAccessLogTable } from "./component/DocumentAccessLogTable";
import { PhysicalClientForm, PhysicalClientFormValues } from "./component/PhysicalClientForm";
import { toUpdateFormValues, toUpdatePayload } from "./component/physicalClientFormSchema";
import { PhysicalClientHistoryTable } from "./component/PhysicalClientHistoryTable";
import { ORIGIN_LABELS, STATUS_ACTION_LABELS } from "./component/physicalClientLabels";
import {
  physicalClientAccessLogQueryKey,
  physicalClientDetailQueryKey,
  physicalClientHistoryQueryKey,
  physicalClientsQueryKey,
} from "./component/physicalClientQueryKeys";
import { PhysicalClientStatusBadge } from "./component/PhysicalClientStatusBadge";
import { PhysicalClientStatusDialog } from "./component/PhysicalClientStatusDialog";
import { PhysicalClientStatusNotice } from "./component/PhysicalClientStatusNotice";
import { ViewCpfButton } from "./component/ViewCpfButton";

const TABS = ["data", "addresses", "access", "history"] as const;
type Tab = (typeof TABS)[number];

/** Aba na URL (?aba=enderecos): recarregar ou compartilhar o link mantém a aba aberta. */
const TAB_SLUGS: Record<Tab, string> = {
  data: "dados",
  addresses: "enderecos",
  access: "acessos",
  history: "historico",
};

const tabFromSlug = (slug: string | null): Tab | undefined =>
  TABS.find((tab) => TAB_SLUGS[tab] === slug);

const STATUSES: PhysicalClientStatus[] = ["Active", "Inactive", "Blocked"];

/**
 * Backoffice > Clientes > Pessoa física > detalhe (/backoffice/clientes/pf/:id, id do cadastro, nunca o CPF).
 * Cabeçalho com CPF mascarado e "Ver CPF", ações de situação e abas de dados, endereços, acessos ao CPF e histórico.
 */
export default function PhysicalClientDetails() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  // O cadastro novo chega com state.tab (vai direto para endereços); depois a aba fica na URL.
  const stateTab = (location.state as { tab?: Tab } | null)?.tab;
  const tab = tabFromSlug(searchParams.get("aba")) ?? (stateTab && TABS.includes(stateTab) ? stateTab : "data");
  const changeTab = (value: string) => setSearchParams({ aba: TAB_SLUGS[value as Tab] }, { replace: true });
  const [statusTarget, setStatusTarget] = useState<PhysicalClientStatus | null>(null);
  const [serverErrors, setServerErrors] = useState<FieldError[]>([]);
  const [formDirty, setFormDirty] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  // Muda a cada salvamento: o formulário volta para os dados salvos mesmo se a API devolver o mesmo cadastro.
  const [savedVersion, setSavedVersion] = useState(0);
  const handleDirtyChange = useCallback((dirty: boolean) => setFormDirty(dirty), []);

  const detailKey = physicalClientDetailQueryKey(id);
  const query = useQuery({
    queryKey: detailKey,
    queryFn: () => physicalClientsApi.getById(id),
    enabled: Number.isInteger(id) && id > 0,
  });
  const client = query.data;
  const formValues = useMemo<PhysicalClientFormValues | null>(
    () => (client ? { cpf: "", ...toUpdateFormValues(client) } : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- savedVersion só força um objeto novo (reset do formulário).
    [client, savedVersion],
  );

  // Fechar ou recarregar a aba do navegador com alterações não salvas pede confirmação.
  useEffect(() => {
    if (!formDirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [formDirty]);

  const refreshClient = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: physicalClientsQueryKey }),
      queryClient.invalidateQueries({ queryKey: physicalClientHistoryQueryKey(id) }),
    ]);

  // O formulário trata os erros de campo; aqui o sucesso e o toast.
  const updateMutation = useMutation({
    mutationFn: (payload: UpdatePhysicalClientDto) => physicalClientsApi.update(id, payload),
    onSuccess: async () => {
      toast.success("Cadastro atualizado.");
      setSavedVersion((version) => version + 1);
      await refreshClient();
    },
    onError: (error) => {
      setServerErrors(getFieldErrors(error));
      toast.error(getCustomerRegistrationErrorMessage(error, "Não foi possível salvar o cadastro."));
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ status, comment }: { status: PhysicalClientStatus; comment: string }) =>
      physicalClientsApi.changeStatus(id, status, comment),
    onSuccess: async () => {
      toast.success("Situação alterada.");
      setStatusTarget(null);
      await refreshClient();
    },
    onError: (error) => toast.error(getCustomerRegistrationErrorMessage(error, "Não foi possível alterar a situação.")),
  });

  const leave = () => navigate("/backoffice/clientes/pf");
  const goBack = () => (formDirty ? setConfirmLeave(true) : leave());

  if (!Number.isInteger(id) || id <= 0 || query.isError) {
    return (
      <div className="p-4 md:p-6">
        <ErrorState
          message="Não foi possível carregar o cliente. Ele pode não existir."
          onRetry={query.isError ? () => void query.refetch() : undefined}
        />
        <Button variant="link" onClick={leave}>
          Voltar para a lista
        </Button>
      </div>
    );
  }

  if (query.isLoading || !client || !formValues) {
    return (
      <div className="space-y-4 p-4 md:p-6" aria-busy="true">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <PhysicalClientStatusDialog
        target={statusTarget}
        clientName={client.fullName}
        isSubmitting={statusMutation.isPending}
        onOpenChange={(open) => !open && setStatusTarget(null)}
        onConfirm={(status, comment) => statusMutation.mutateAsync({ status, comment }).catch(() => undefined)}
      />

      <AlertDialog open={confirmLeave} onOpenChange={setConfirmLeave}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sair sem salvar?</AlertDialogTitle>
            <AlertDialogDescription>
              Os dados do cliente têm alterações não salvas. Se sair agora, elas serão perdidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction onClick={leave}>Sair sem salvar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={goBack}>
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            Voltar
          </Button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{client.fullName}</h1>
            <PhysicalClientStatusBadge status={client.status} />
          </div>
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
            <dt className="text-muted-foreground">CPF</dt>
            <dd>
              <ViewCpfButton
                physicalClientId={client.id}
                cpfMasked={client.cpfMasked}
                accessLogQueryKey={physicalClientAccessLogQueryKey(id)}
              />
            </dd>
            <dt className="text-muted-foreground">Grupo</dt>
            <dd>{client.customerGroup?.name ?? "Sem grupo"}</dd>
            <dt className="text-muted-foreground">Origem</dt>
            <dd>{ORIGIN_LABELS[client.origin] ?? client.origin}</dd>
            <dt className="text-muted-foreground">Cadastrado em</dt>
            <dd>
              {formatDateTime(new Date(client.createdAt))} por {client.createdBy}
            </dd>
            {client.updatedAt && (
              <>
                <dt className="text-muted-foreground">Atualizado em</dt>
                <dd>
                  {formatDateTime(new Date(client.updatedAt))}
                  {client.updatedBy && <> por {client.updatedBy}</>}
                </dd>
              </>
            )}
          </dl>
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUSES.filter((status) => status !== client.status).map((status) => (
            <Button
              key={status}
              variant={status === "Active" ? "default" : "outline"}
              onClick={() => setStatusTarget(status)}
            >
              {STATUS_ACTION_LABELS[status]}
            </Button>
          ))}
        </div>
      </div>

      <PhysicalClientStatusNotice physicalClientId={client.id} status={client.status} />

      <Tabs value={tab} onValueChange={changeTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
          <TabsTrigger value="data" className="gap-1.5">
            Dados
            {formDirty && (
              <>
                <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
                <span className="sr-only">(alterações não salvas)</span>
              </>
            )}
          </TabsTrigger>
          <TabsTrigger value="addresses">Endereços</TabsTrigger>
          <TabsTrigger value="access">Acessos ao CPF</TabsTrigger>
          <TabsTrigger value="history">Histórico</TabsTrigger>
        </TabsList>

        {/* Fica montada ao trocar de aba, para não perder o que foi digitado e ainda não salvo. */}
        <TabsContent value="data" forceMount className="data-[state=inactive]:hidden">
          <Card className="max-w-3xl">
            <CardHeader>
              <CardTitle>Dados do cliente</CardTitle>
              <CardDescription>O CPF não pode ser alterado. A situação muda pelos botões acima.</CardDescription>
            </CardHeader>
            <CardContent>
              <PhysicalClientForm
                mode="edit"
                defaultValues={formValues}
                isSubmitting={updateMutation.isPending}
                serverErrors={serverErrors}
                onSubmit={(values) => {
                  setServerErrors([]);
                  updateMutation.mutate(toUpdatePayload({ ...values, comment: values.comment ?? "" }));
                }}
                onDirtyChange={handleDirtyChange}
                submitLabel="Salvar alterações"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="addresses">
          <PhysicalClientAddressList physicalClientId={client.id} physicalClientQueryKey={detailKey} />
        </TabsContent>

        <TabsContent value="access">
          <Card>
            <CardHeader>
              <CardTitle>Acessos ao CPF completo</CardTitle>
              <CardDescription>Cada "Ver CPF" fica registrado aqui (quem, quando e o motivo informado).</CardDescription>
            </CardHeader>
            <CardContent>
              <DocumentAccessLogTable physicalClientId={client.id} queryKey={physicalClientAccessLogQueryKey(id)} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Histórico</CardTitle>
              <CardDescription>Cadastro, edições, mudanças de situação e de endereços.</CardDescription>
            </CardHeader>
            <CardContent>
              <PhysicalClientHistoryTable physicalClientId={client.id} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
